import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Users,
  Target,
  Link,
  Search,
  Send,
  CheckSquare,
  XCircle,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

const ESRSDisclosuresWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // State for header chips
  const [selectedStandards] = useState(['ESRS2', 'E1', 'S1', 'G1']);
  const [completeness, setCompleteness] = useState(0);
  const [outOfSync, setOutOfSync] = useState(0);
  const [legalStatus, setLegalStatus] = useState('pending');

  // State for standards rail
  const [selectedStandard, setSelectedStandard] = useState('ESRS2');

  // State for disclosure editor
  const [disclosureContent, setDisclosureContent] = useState('');
  const [checklistItems, setChecklistItems] = useState([]);

  // State for validation
  const [validationResults, setValidationResults] = useState([]);
  const [legalReviewer, setLegalReviewer] = useState('');
  const [legalApproval, setLegalApproval] = useState('pending');

  // Available standards
  const standards = [
    { id: 'ESRS2', name: 'ESRS 2', required: true, progress: 75 },
    { id: 'E1', name: 'E1 Climate', required: false, progress: 60 },
    { id: 'S1', name: 'S1 Workforce', required: false, progress: 40 },
    { id: 'G1', name: 'G1 Business Conduct', required: false, progress: 30 },
    { id: 'E2', name: 'E2 Pollution', required: false, progress: 0 },
    { id: 'E3', name: 'E3 Water', required: false, progress: 0 },
    { id: 'E4', name: 'E4 Biodiversity', required: false, progress: 0 },
    { id: 'E5', name: 'E5 Circular Economy', required: false, progress: 0 },
    { id: 'S2', name: 'S2 Value Chain', required: false, progress: 0 },
    { id: 'S3', name: 'S3 Communities', required: false, progress: 0 },
    { id: 'S4', name: 'S4 Consumers', required: false, progress: 0 }
  ];

  // Smart fields tokens
  const smartFields = [
    '{{Scope1_total}}',
    '{{Scope2_LB_total}}',
    '{{Scope2_MB_total}}',
    '{{S3_total}}',
    '{{Energy_intensity}}',
    '{{Carbon_intensity}}',
    '{{Targets_table}}',
    '{{KPIs_table}}',
    '{{S1_by_site}}',
    '{{S2_by_site}}',
    '{{S3_by_category}}'
  ];

  // Sample checklist items for ESRS2
  const esrs2Checklist = [
    { id: 'gov', name: 'Governance', required: true, completed: true },
    { id: 'strategy', name: 'Strategy', required: true, completed: true },
    { id: 'iro', name: 'Impact, Risk & Opportunity', required: true, completed: true },
    { id: 'mt', name: 'Metrics & Targets', required: true, completed: false }
  ];

  // Sample evidence links
  const sampleEvidenceLinks = [
    { name: 'Climate Policy', owner: 'ESG Team', lastUpdated: '2024-01-15', status: 'Current' },
    { name: 'Energy Management Plan', owner: 'Operations', lastUpdated: '2023-06-20', status: 'Outdated' },
    { name: 'Sustainability Report 2023', owner: 'Communications', lastUpdated: '2024-03-01', status: 'Current' }
  ];

  const handleStandardSelect = (standardId) => {
    setSelectedStandard(standardId);
    // Update checklist based on selected standard
    if (standardId === 'ESRS2') {
      setChecklistItems(esrs2Checklist);
    } else {
      setChecklistItems([]);
    }
  };

  const handleInsertToken = (token) => {
    setDisclosureContent(prev => prev + token);
  };

  const handleRunCompletenessCheck = () => {
    const results = [
      { type: 'error', message: 'Target missing baseline year.' },
      { type: 'error', message: 'Number out of sync with data_freeze_v1.' },
      { type: 'warning', message: 'Policy last updated > 24 months.' },
      { type: 'info', message: 'Missing evidence for Scope 3 calculations.' }
    ];
    setValidationResults(results);
    
    // Update header chips
    setCompleteness(65);
    setOutOfSync(2);
  };

  const handleSendToLegal = () => {
    setLegalStatus('underReview');
  };

  const handleMarkLegalApproved = () => {
    setLegalStatus('approved');
    setLegalApproval('approved');
  };

  const isDefinitionOfDone = () => {
    return completeness >= 100 && 
           outOfSync === 0 && 
           legalStatus === 'approved' &&
           checklistItems.every(item => !item.required || item.completed);
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'text-green-600';
    if (progress >= 75) return 'text-blue-600';
    if (progress >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getValidationIcon = (type) => {
    switch (type) {
      case 'error': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'info': return <AlertCircle className="w-4 h-4 text-blue-600" />;
      default: return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 relative">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('workflow.esrsDisclosures.title')}</h1>
          <p className="text-gray-600 mt-2">{t('workflow.esrsDisclosures.subtitle')}</p>
        </div>
      </div>

      {/* Header Chips */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          <FileText className="w-4 h-4" />
          {t('workflow.esrsDisclosures.selected')}: {selectedStandards.join(', ')}
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
          completeness >= 100 ? 'bg-green-100 text-green-800' : 
          completeness >= 75 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
        }`}>
          <CheckCircle className="w-4 h-4" />
          {t('workflow.esrsDisclosures.completeness')}: {completeness}%
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
          outOfSync === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          <AlertTriangle className="w-4 h-4" />
          {t('workflow.esrsDisclosures.outOfSync')}: {outOfSync}
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
          legalStatus === 'approved' ? 'bg-green-100 text-green-800' : 
          legalStatus === 'underReview' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
        }`}>
          <Users className="w-4 h-4" />
          {t('workflow.esrsDisclosures.legal')}: {t(`workflow.esrsDisclosures.validationReview.statusOptions.${legalStatus}`)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* A) Standards Rail */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('workflow.esrsDisclosures.standardsRail.title')}
            </h2>
            
            <div className="space-y-2">
              {standards.map((standard) => (
                <div
                  key={standard.id}
                  onClick={() => handleStandardSelect(standard.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedStandard === standard.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{standard.name}</span>
                    {standard.required && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                        {t('workflow.esrsDisclosures.required')}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          standard.progress >= 100 ? 'bg-green-500' :
                          standard.progress >= 75 ? 'bg-blue-500' :
                          standard.progress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${standard.progress}%` }}
                      />
                    </div>
                    <span className={`text-sm font-medium ${getProgressColor(standard.progress)}`}>
                      {standard.progress}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* B) Disclosure Editor */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('workflow.esrsDisclosures.disclosureEditor.title')} - {standards.find(s => s.id === selectedStandard)?.name}
            </h2>

            {/* Checklist */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                {t('workflow.esrsDisclosures.disclosureEditor.checklist')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {checklistItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      item.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                    }`}>
                      {item.completed && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{item.name}</span>
                      {item.required && (
                        <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          {t('workflow.esrsDisclosures.required')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Smart Fields */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                {t('workflow.esrsDisclosures.disclosureEditor.smartFields')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {smartFields.map((field) => (
                  <button
                    key={field}
                    onClick={() => handleInsertToken(field)}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 text-sm font-mono"
                  >
                    {field}
                  </button>
                ))}
              </div>
            </div>

            {/* Editor */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                {t('workflow.esrsDisclosures.disclosureEditor.editor')}
              </h3>
              <textarea
                value={disclosureContent}
                onChange={(e) => setDisclosureContent(e.target.value)}
                className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder={t('workflow.esrsDisclosures.disclosureEditor.editorPlaceholder')}
              />
            </div>

            {/* Evidence Links */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                {t('workflow.esrsDisclosures.disclosureEditor.evidenceLinks')}
              </h3>
              <div className="space-y-3">
                {sampleEvidenceLinks.map((link, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{link.name}</div>
                      <div className="text-sm text-gray-600">
                        {t('workflow.esrsDisclosures.disclosureEditor.owner')}: {link.owner} • 
                        {t('workflow.esrsDisclosures.disclosureEditor.lastUpdated')}: {link.lastUpdated}
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      link.status === 'Current' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {link.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {/* Insert numbers logic */}}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Target className="w-4 h-4" />
                {t('workflow.esrsDisclosures.insertNumbers')}
              </button>
          
              <button
                onClick={() => {/* Link evidence logic */}}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                <Link className="w-4 h-4" />
                {t('workflow.esrsDisclosures.linkEvidence')}
              </button>
              
              <button
                onClick={handleRunCompletenessCheck}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
              >
                <Search className="w-4 h-4" />
                {t('workflow.esrsDisclosures.runCompletenessCheck')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* C) Validation & Review */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('workflow.esrsDisclosures.validationReview.title')}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Validation Results */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-3">
              {t('workflow.esrsDisclosures.validationReview.validationResults')}
            </h3>
            <div className="space-y-2">
              {validationResults.map((result, index) => (
                <div key={index} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                  {getValidationIcon(result.type)}
                  <span className="text-sm text-gray-700">{result.message}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Legal Review */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-3">
              {t('workflow.esrsDisclosures.validationReview.legalReview')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('workflow.esrsDisclosures.validationReview.reviewer')}
                </label>
                <input
                  type="text"
                  value={legalReviewer}
                  onChange={(e) => setLegalReviewer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.esrsDisclosures.validationReview.reviewerPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('workflow.esrsDisclosures.validationReview.status')}
                </label>
                <select
                  value={legalApproval}
                  onChange={(e) => setLegalApproval(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">{t('workflow.esrsDisclosures.validationReview.statusOptions.pending')}</option>
                  <option value="changesRequested">{t('workflow.esrsDisclosures.validationReview.statusOptions.changesRequested')}</option>
                  <option value="approved">{t('workflow.esrsDisclosures.validationReview.statusOptions.approved')}</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleSendToLegal}
                  disabled={legalStatus === 'underReview' || legalStatus === 'approved'}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  {t('workflow.esrsDisclosures.sendToLegal')}
                </button>
                
                <button
                  onClick={handleMarkLegalApproved}
                  disabled={legalApproval === 'approved'}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <CheckSquare className="w-4 h-4" />
                  {t('workflow.esrsDisclosures.markLegalApproved')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          disabled={!isDefinitionOfDone()}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {t('workflow.esrsDisclosures.assembleDraft')}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
      {previewOnly && (
        <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px] z-10 rounded-lg">
        </div>
      )}
    </div>
  );
};

export default ESRSDisclosuresWorkflow;